/**
 * Trabalhe Conosco em etapas — o componente que o site monta no lugar do marcador
 * `itp-tc-form` (lib/tc/placeholder.ts). Copia o caminho da calculadora de avaliação:
 * fala só com o relay same-origin `pages/api/trabalhe-conosco/[acao].ts`, que repassa ao
 * REST público do app (contrato §3).
 *
 * Esteira: 1 Seus dados → 2 Código do e-mail → 3 Currículo (se ativo) → etapas do builder
 * → Revisar e enviar → sucesso. O progresso fica na sessionStorage (chave por
 * codsite+pageId+blockId): recarregar não perde nada; depois do sucesso, limpa.
 *
 * SSR-safe: nada de `window`/`document` no render — o passo 1 sai no HTML do servidor e
 * o estado salvo só é restaurado num efeito, depois da montagem.
 */
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TcConfig, TcCurriculoOk, TcEnviarOk, TcIniciarOk, TcReenviarOk, TcVaga, TcVagasOk, TcVerificarOk } from '../../lib/tc/types';
import { getAccessibleTextColor, getContrastRatio } from '../../utils/FunctionsGlobals';
import { corComAlfa } from '../avm/avm.utils';
import { Icone, useIdEstavel } from '../avm/AvmUI';
import type { TcDados, TcErros, TcEstado, TcRetorno } from './tc.utils';
import {
    camposDoErro,
    chamarTc,
    chaveStorage,
    codigoDoErro,
    decorridoDesde,
    ehNaoAutorizado,
    MENSAGEM_DUPLICADO,
    MENSAGEM_INDISPONIVEL,
    normalizarVerificacaoId,
    conferirPdf,
    enviarCurriculo,
    INDICE_CODIGO,
    INDICE_DADOS,
    lerOuGravarT0,
    limparEstado,
    mensagemDoErro,
    mesmosDados,
    montarPassos,
    passoDoCampo,
    primeiroNome,
    restaurarEstado,
    salvarEstado,
    TC_ESTADO_INICIAL,
    tokenValido,
    validarCampo,
    validarDados,
    validarEtapa,
} from './tc.utils';
import { PassoCodigo, PassoCurriculo, PassoDados, PassoEtapa, PassoRevisao, TAMANHO_CODIGO, TelaSucesso } from './TcPassos';
import styles from './TrabalheConosco.module.css';

export interface TrabalheConoscoProps {
    config: TcConfig;
    /** HTML interno do marcador (contato da imobiliária): volta à tela se o app disser INDISPONIVEL. */
    fallbackHtml?: string;
}

const MENSAGEM_REDE = 'Sem conexão com o servidor. Confira sua internet e tente de novo.';

const TrabalheConosco = ({ config, fallbackHtml }: TrabalheConoscoProps) => {
    const idBase = useIdEstavel('tc');
    const passos = useMemo(() => montarPassos(config), [config]);
    const total = passos.length;
    const chave = useMemo(() => chaveStorage(config), [config]);
    const auth = useMemo(() => ({ codsite: config.codsite, siteToken: config.token }), [config.codsite, config.token]);

    const [estado, setEstadoReact] = useState<TcEstado>(TC_ESTADO_INICIAL);
    // Espelho SÍNCRONO do estado: handlers encadeados (blur → validação, upload → token)
    // leem daqui, sem esperar o próximo render.
    const estadoRef = useRef<TcEstado>(TC_ESTADO_INICIAL);
    const setEstado = useCallback((mudanca: Partial<TcEstado> | ((_atual: TcEstado) => Partial<TcEstado>)) => {
        const parcial = typeof mudanca === 'function' ? mudanca(estadoRef.current) : mudanca;
        estadoRef.current = { ...estadoRef.current, ...parcial };
        setEstadoReact(estadoRef.current);
    }, []);

    const [hp, setHp] = useState('');
    const [erros, setErros] = useState<TcErros>({});
    const [erroEnvio, setErroEnvio] = useState<string | null>(null);
    const [aviso, setAviso] = useState<string | null>(null);
    const [enviando, setEnviando] = useState(false);
    const [sucesso, setSucesso] = useState(false);
    const [indisponivel, setIndisponivel] = useState<string | null>(null);
    const [duplicado, setDuplicado] = useState(false);
    const [vagas, setVagas] = useState<TcVaga[] | null>(config.mostrarVagas ? null : []);
    const [codigo, setCodigo] = useState('');
    const [contador, setContador] = useState(0);
    const [reenviando, setReenviando] = useState(false);
    const [progresso, setProgresso] = useState<number | null>(null);
    const [nomeEnviando, setNomeEnviando] = useState<string | null>(null);
    const [reduzido, setReduzido] = useState(false);
    const [restaurado, setRestaurado] = useState(false);

    const t0Ref = useRef(0);
    const cardRef = useRef<HTMLDivElement | null>(null);
    const corpoRef = useRef<HTMLDivElement | null>(null);
    const primeiroRenderRef = useRef(true);
    const cancelarUploadRef = useRef<(() => void) | null>(null);
    const verificandoRef = useRef(false);

    const passo = estado.passo;
    const atual = passos[passo] || passos[0];
    const travado = enviando || progresso !== null;

    /* ── ambiente (só depois da montagem: o render continua SSR-safe) ────────── */

    useEffect(() => {
        t0Ref.current = lerOuGravarT0(chave);

        const salvo = restaurarEstado(chave, total);
        if (salvo) {
            estadoRef.current = salvo;
            setEstadoReact(salvo);
        }
        setRestaurado(true);

        const mq = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
        if (!mq) return;
        const aplicar = () => setReduzido(!!mq.matches);
        aplicar();
        if (typeof mq.addEventListener === 'function') {
            mq.addEventListener('change', aplicar);
            return () => mq.removeEventListener('change', aplicar);
        }
        if (typeof (mq as any).addListener === 'function') {
            (mq as any).addListener(aplicar);
            return () => (mq as any).removeListener(aplicar);
        }
        return;
    }, [chave, total]);

    /* ── persistência (sessionStorage) ──────────────────────────────────────── */

    useEffect(() => {
        if (!restaurado || sucesso || duplicado) return;
        salvarEstado(chave, estado);
    }, [estado, restaurado, sucesso, duplicado, chave]);

    useEffect(
        () => () => {
            if (cancelarUploadRef.current) cancelarUploadRef.current();
        },
        [],
    );

    /* ── vagas abertas (chips do passo 1) ───────────────────────────────────── */

    useEffect(() => {
        if (!config.mostrarVagas) return;
        let ativo = true;
        chamarTc<TcVagasOk>('vagas', auth)
            .then((r) => {
                if (!ativo) return;
                if (r.json && (r.json as TcVagasOk).ok === true && Array.isArray((r.json as TcVagasOk).vagas)) {
                    const lista = (r.json as TcVagasOk).vagas
                        .filter((v) => v && Number.isInteger(v.id) && typeof v.titulo === 'string' && v.titulo.trim())
                        .map((v) => ({ id: v.id, titulo: v.titulo.trim(), descricao: typeof v.descricao === 'string' ? v.descricao : null }));
                    setVagas(lista);
                    return;
                }
                if (codigoDoErro(r.json) === 'INDISPONIVEL') setIndisponivel(mensagemDoErro(r.json, ''));
                setVagas([]); // sem lista (ou falha): os chips somem e a esteira segue
            })
            .catch(() => {
                if (ativo) setVagas([]);
            });
        return () => {
            ativo = false;
        };
    }, [config.mostrarVagas, auth]);

    /* ── contagem do "Reenviar código" ──────────────────────────────────────── */

    useEffect(() => {
        if (contador <= 0) return;
        const timer = setTimeout(() => setContador((c) => Math.max(0, c - 1)), 1000);
        return () => clearTimeout(timer);
    }, [contador]);

    /* ── ao trocar de passo: rola (se precisar) e foca o 1º campo ───────────── */

    useEffect(() => {
        if (primeiroRenderRef.current) {
            primeiroRenderRef.current = false;
            return;
        }
        const card = cardRef.current;
        if (card && typeof card.getBoundingClientRect === 'function' && card.getBoundingClientRect().top < 0) {
            try {
                card.scrollIntoView({ behavior: reduzido ? 'auto' : 'smooth', block: 'start' });
            } catch {
                card.scrollIntoView();
            }
        }
        const corpo = corpoRef.current;
        if (!corpo || sucesso) return;
        const timer = setTimeout(() => {
            const alvo = corpo.querySelector<HTMLElement>(
                'input:not([type="hidden"]):not([tabindex="-1"]):not([disabled]), textarea:not([disabled]), [role="radio"]:not([disabled]), [role="checkbox"]:not([disabled]), button[id]:not([disabled])',
            );
            if (alvo && typeof alvo.focus === 'function') alvo.focus({ preventScroll: true } as any);
        }, reduzido ? 0 : 120);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [passo, sucesso]);

    /* ── estado do formulário ───────────────────────────────────────────────── */

    const limparErro = useCallback((chaveErro: string) => {
        setErros((atuais) => {
            if (!atuais[chaveErro]) return atuais;
            const novos = { ...atuais };
            delete novos[chaveErro];
            return novos;
        });
    }, []);

    const setDado = useCallback(
        <K extends keyof TcDados>(campo: K, valor: TcDados[K]) => {
            setEstado((e) => ({ dados: { ...e.dados, [campo]: valor } }));
            limparErro(campo);
            setErroEnvio(null);
        },
        [setEstado, limparErro],
    );

    const setResposta = useCallback(
        (chaveCampo: string, valor: string | string[]) => {
            setEstado((e) => ({ respostas: { ...e.respostas, [chaveCampo]: valor } }));
            limparErro(chaveCampo);
        },
        [setEstado, limparErro],
    );

    const aoSairDado = useCallback((campo: keyof TcDados) => {
        const todos = validarDados(estadoRef.current.dados);
        setErros((atuais) => {
            const novos = { ...atuais };
            if (todos[campo]) novos[campo] = todos[campo];
            else delete novos[campo];
            return novos;
        });
    }, []);

    const aoSairResposta = useCallback(
        (chaveCampo: string) => {
            const def = atual.tipo === 'etapa' ? atual.etapa.campos.find((c) => c.key === chaveCampo) : null;
            if (!def) return;
            const erro = validarCampo(def, estadoRef.current.respostas[chaveCampo]);
            setErros((atuais) => {
                const novos = { ...atuais };
                if (erro) novos[chaveCampo] = erro;
                else delete novos[chaveCampo];
                return novos;
            });
        },
        [atual],
    );

    const focarPrimeiroErro = useCallback(
        (encontrados: TcErros) => {
            const corpo = corpoRef.current;
            const primeira = Object.keys(encontrados)[0];
            if (!corpo || !primeira) return;
            const id = ['nome', 'email', 'celular', 'cidade', 'vagas', 'codigo', 'curriculo', 'consentimento'].indexOf(primeira) !== -1
                ? `${idBase}-${primeira}`
                : `${idBase}-c-${primeira}`;
            const alvo = corpo.querySelector<HTMLElement>(`[id="${id}"]`) || corpo.querySelector<HTMLElement>(`[id="${id}-grupo"] button`);
            if (alvo && typeof alvo.focus === 'function') alvo.focus({ preventScroll: false } as any);
        },
        [idBase],
    );

    const irPara = useCallback(
        (proximo: number) => {
            setEstado({ passo: Math.max(0, Math.min(total - 1, proximo)) });
            setErros({});
            setErroEnvio(null);
        },
        [setEstado, total],
    );

    /** Sessão do app acabou (TOKEN_INVALIDO / token vencido): volta ao código sem perder as respostas. */
    const voltarAoCodigo = useCallback(
        (mensagem: string) => {
            setEstado({ tcToken: null, tcExpiraEm: null, curriculo: null, dadosVerificados: null });
            setCodigo('');
            setContador(0);
            setAviso(mensagem);
            irPara(estadoRef.current.verificacaoId ? INDICE_CODIGO : INDICE_DADOS);
        },
        [setEstado, irPara],
    );

    /** DUPLICADO no /verificar ou /enviar: tela final gentil, limpa o rascunho e não volta ao passo 1. */
    const telaDuplicado = useCallback(() => {
        limparEstado(chave);
        setDuplicado(true);
    }, [chave]);

    /**
     * Erros do contrato (§3) que valem para qualquer rota. Devolve true quando tratou.
     * Os específicos de cada passo (CODIGO_*, PDF_*) ficam no chamador.
     */
    const tratarErroComum = useCallback(
        (r: TcRetorno<any>, padrao: string): boolean => {
            const cod = codigoDoErro(r.json);
            // 401 do token do site: status ANTES do `erro` (TOKEN_INVALIDO só vale sem 401)
            if (ehNaoAutorizado(r.status, r.json)) {
                setIndisponivel(MENSAGEM_INDISPONIVEL);
                return true;
            }
            if (cod === 'INDISPONIVEL') {
                setIndisponivel(mensagemDoErro(r.json, ''));
                return true;
            }
            if (cod === 'TOKEN_INVALIDO') {
                voltarAoCodigo(mensagemDoErro(r.json, ''));
                return true;
            }
            if (cod === 'VALIDACAO') {
                const campos = camposDoErro(r.json);
                const chaves = Object.keys(campos);
                const alvo = chaves.length ? passoDoCampo(passos, chaves[0]) : -1;
                if (alvo >= 0) {
                    irPara(alvo);
                    setErros(campos);
                    setTimeout(() => focarPrimeiroErro(campos), reduzido ? 0 : 320);
                }
                setErroEnvio(mensagemDoErro(r.json, padrao));
                return true;
            }
            if (cod === 'DUPLICADO' || cod === 'LIMITE') {
                setErroEnvio(mensagemDoErro(r.json, padrao));
                return true;
            }
            if (!r.json || typeof r.json !== 'object') {
                setErroEnvio(padrao);
                return true;
            }
            return false;
        },
        [passos, irPara, focarPrimeiroErro, voltarAoCodigo, reduzido],
    );

    /* ── 1. iniciar (pede o código) ──────────────────────────────────────────── */

    const iniciar = useCallback(async () => {
        const e = estadoRef.current;
        setEnviando(true);
        setErroEnvio(null);
        try {
            const r = await chamarTc<TcIniciarOk>('iniciar', auth, {
                pageId: config.pageId,
                blockId: config.blockId,
                nome: e.dados.nome.trim(),
                email: e.dados.email.trim(),
                celular: e.dados.celular,
                cidade: e.dados.cidade.trim() || undefined,
                __hp: hp,
                decorridoMs: decorridoDesde(t0Ref.current),
            });
            const json = r.json as TcIniciarOk | null;
            if (json && json.ok === true && normalizarVerificacaoId(json.verificacaoId)) {
                setEstado({
                    verificacaoId: normalizarVerificacaoId(json.verificacaoId),
                    emailMascarado: typeof json.emailMascarado === 'string' ? json.emailMascarado : null,
                    tcToken: null,
                    tcExpiraEm: null,
                    curriculo: null,
                    dadosVerificados: { ...e.dados, vagas: [] },
                });
                setCodigo('');
                setAviso(null);
                setContador(Number.isFinite(json.reenvioEmSeg) ? Number(json.reenvioEmSeg) : 60);
                irPara(INDICE_CODIGO);
                return;
            }
            if (!tratarErroComum(r, 'Não conseguimos enviar o código agora. Tente de novo em instantes.')) {
                setErroEnvio(mensagemDoErro(r.json, 'Não conseguimos enviar o código agora. Tente de novo em instantes.'));
            }
        } catch {
            setErroEnvio(MENSAGEM_REDE);
        } finally {
            setEnviando(false);
        }
    }, [auth, config.pageId, config.blockId, hp, setEstado, irPara, tratarErroComum]);

    /* ── 2. verificar / reenviar / trocar e-mail ─────────────────────────────── */

    const verificar = useCallback(
        async (cod: string) => {
            const e = estadoRef.current;
            if (verificandoRef.current) return;
            if (!e.verificacaoId) {
                irPara(INDICE_DADOS);
                return;
            }
            verificandoRef.current = true;
            setEnviando(true);
            setErroEnvio(null);
            setAviso(null);
            try {
                const r = await chamarTc<TcVerificarOk>('verificar', auth, { verificacaoId: e.verificacaoId, codigo: cod });
                const json = r.json as TcVerificarOk | null;
                if (json && json.ok === true && typeof json.tcToken === 'string' && json.tcToken) {
                    const seg = Number.isFinite(json.expiraEmSeg) ? Number(json.expiraEmSeg) : 3600;
                    setEstado({ tcToken: json.tcToken, tcExpiraEm: Date.now() + seg * 1000 });
                    setCodigo('');
                    irPara(INDICE_CODIGO + 1);
                    return;
                }
                const codErro = codigoDoErro(r.json);
                if (codErro === 'DUPLICADO' && !ehNaoAutorizado(r.status, r.json)) {
                    telaDuplicado();
                    return;
                }
                if (codErro === 'CODIGO_INVALIDO' || codErro === 'CODIGO_EXPIRADO' || codErro === 'TENTATIVAS') {
                    setErros({ codigo: mensagemDoErro(r.json, '') });
                    setCodigo('');
                    if (codErro !== 'CODIGO_INVALIDO') setContador(0); // libera o reenvio na hora
                    setTimeout(() => focarPrimeiroErro({ codigo: 'x' }), 0);
                    return;
                }
                if (!tratarErroComum(r, 'Não conseguimos confirmar o código agora. Tente de novo.')) {
                    setErroEnvio(mensagemDoErro(r.json, 'Não conseguimos confirmar o código agora. Tente de novo.'));
                }
            } catch {
                setErroEnvio(MENSAGEM_REDE);
            } finally {
                verificandoRef.current = false;
                setEnviando(false);
            }
        },
        [auth, setEstado, irPara, tratarErroComum, focarPrimeiroErro, telaDuplicado],
    );

    const reenviar = useCallback(async () => {
        const e = estadoRef.current;
        if (!e.verificacaoId || reenviando) return;
        setReenviando(true);
        setErroEnvio(null);
        setErros({});
        try {
            const r = await chamarTc<TcReenviarOk>('reenviar', auth, { verificacaoId: e.verificacaoId });
            const json = r.json as TcReenviarOk | null;
            if (json && json.ok === true) {
                setCodigo('');
                setContador(Number.isFinite(json.reenvioEmSeg) ? Number(json.reenvioEmSeg) : 60);
                setAviso(`Enviamos um novo código para ${e.emailMascarado || 'o seu e-mail'}.`);
                return;
            }
            if (codigoDoErro(r.json) === 'LIMITE') {
                setContador(Math.max(contador, 60));
            }
            if (!tratarErroComum(r, 'Não conseguimos reenviar agora. Tente de novo em instantes.')) {
                setErroEnvio(mensagemDoErro(r.json, 'Não conseguimos reenviar agora. Tente de novo em instantes.'));
            }
        } catch {
            setErroEnvio(MENSAGEM_REDE);
        } finally {
            setReenviando(false);
        }
    }, [auth, reenviando, contador, tratarErroComum]);

    const trocarEmail = useCallback(() => {
        setEstado({ verificacaoId: null, emailMascarado: null, tcToken: null, tcExpiraEm: null, curriculo: null, dadosVerificados: null });
        setCodigo('');
        setContador(0);
        setAviso(null);
        irPara(INDICE_DADOS);
        setTimeout(() => focarPrimeiroErro({ email: 'x' }), reduzido ? 0 : 320);
    }, [setEstado, irPara, focarPrimeiroErro, reduzido]);

    /* ── 3. currículo ────────────────────────────────────────────────────────── */

    const escolherArquivo = useCallback(
        async (arquivo: File) => {
            setErros({});
            setErroEnvio(null);
            const problema = await conferirPdf(arquivo);
            if (problema) {
                setErros({ curriculo: problema });
                return;
            }
            const e = estadoRef.current;
            if (!tokenValido(e)) {
                voltarAoCodigo('Sua sessão expirou. Confirme o seu e-mail de novo para enviar o currículo.');
                return;
            }
            setNomeEnviando(arquivo.name);
            setProgresso(0);
            const { promessa, cancelar } = enviarCurriculo<TcCurriculoOk>(arquivo, { ...auth, tcToken: e.tcToken }, setProgresso);
            cancelarUploadRef.current = cancelar;
            try {
                const r = await promessa;
                const json = r.json as TcCurriculoOk | null;
                if (json && json.ok === true) {
                    setEstado({
                        curriculo: {
                            nome: typeof json.nome === 'string' && json.nome.trim() ? json.nome.trim() : arquivo.name,
                            bytes: Number.isFinite(json.bytes) ? Number(json.bytes) : arquivo.size,
                        },
                    });
                    return;
                }
                const codErro = codigoDoErro(r.json);
                if (codErro === 'PDF_INVALIDO' || codErro === 'PDF_GRANDE' || r.status === 413) {
                    setErros({ curriculo: mensagemDoErro(r.json, 'Não conseguimos aceitar esse arquivo.') });
                    return;
                }
                if (!tratarErroComum(r, 'Não conseguimos enviar o currículo agora. Tente de novo.')) {
                    setErros({ curriculo: mensagemDoErro(r.json, 'Não conseguimos enviar o currículo agora. Tente de novo.') });
                }
            } catch (err: any) {
                if (String(err?.message) !== 'abortado') setErros({ curriculo: MENSAGEM_REDE });
            } finally {
                cancelarUploadRef.current = null;
                setProgresso(null);
                setNomeEnviando(null);
            }
        },
        [auth, setEstado, voltarAoCodigo, tratarErroComum],
    );

    const trocarArquivo = useCallback(() => {
        setEstado({ curriculo: null });
        setErros({});
    }, [setEstado]);

    /* ── último: enviar a candidatura ────────────────────────────────────────── */

    const enviar = useCallback(async () => {
        const e = estadoRef.current;
        if (!e.consentimento) {
            const encontrados = { consentimento: 'Para enviar, precisamos da sua autorização.' };
            setErros(encontrados);
            focarPrimeiroErro(encontrados);
            return;
        }
        if (!tokenValido(e)) {
            voltarAoCodigo('Sua sessão expirou. Confirme o seu e-mail de novo para enviar a candidatura.');
            return;
        }
        setEnviando(true);
        setErroEnvio(null);
        try {
            const r = await chamarTc<TcEnviarOk>(
                'enviar',
                { ...auth, tcToken: e.tcToken },
                { tcToken: e.tcToken, vagas: e.dados.vagas, respostas: e.respostas, consentimento: true },
            );
            const json = r.json as TcEnviarOk | null;
            if (json && json.ok === true) {
                limparEstado(chave);
                setSucesso(true);
                return;
            }
            if (codigoDoErro(r.json) === 'DUPLICADO' && !ehNaoAutorizado(r.status, r.json)) {
                telaDuplicado();
                return;
            }
            if (!tratarErroComum(r, 'Não conseguimos enviar a candidatura agora. Tente de novo em instantes.')) {
                setErroEnvio(mensagemDoErro(r.json, 'Não conseguimos enviar a candidatura agora. Tente de novo em instantes.'));
            }
        } catch {
            setErroEnvio(MENSAGEM_REDE);
        } finally {
            setEnviando(false);
        }
    }, [auth, chave, focarPrimeiroErro, voltarAoCodigo, tratarErroComum, telaDuplicado]);

    /* ── navegação ───────────────────────────────────────────────────────────── */

    const continuar = useCallback(() => {
        const e = estadoRef.current;
        if (travado) return;
        setAviso(null);
        switch (atual.tipo) {
            case 'dados': {
                const encontrados = validarDados(e.dados);
                setErros(encontrados);
                if (Object.keys(encontrados).length) {
                    focarPrimeiroErro(encontrados);
                    return;
                }
                // já verificado e nada mudou: não manda outro e-mail
                if (tokenValido(e) && mesmosDados(e.dados, e.dadosVerificados)) {
                    irPara(INDICE_CODIGO + 1);
                    return;
                }
                iniciar();
                return;
            }
            case 'codigo': {
                if (codigo.length !== TAMANHO_CODIGO) {
                    const encontrados = { codigo: `Digite os ${TAMANHO_CODIGO} dígitos do código.` };
                    setErros(encontrados);
                    focarPrimeiroErro(encontrados);
                    return;
                }
                verificar(codigo);
                return;
            }
            case 'curriculo': {
                if (config.curriculo.obrigatorio && !e.curriculo) {
                    const encontrados = { curriculo: 'Envie o seu currículo em PDF para continuar.' };
                    setErros(encontrados);
                    focarPrimeiroErro(encontrados);
                    return;
                }
                irPara(passo + 1);
                return;
            }
            case 'etapa': {
                const encontrados = validarEtapa(atual.etapa, e.respostas);
                setErros(encontrados);
                if (Object.keys(encontrados).length) {
                    focarPrimeiroErro(encontrados);
                    return;
                }
                irPara(passo + 1);
                return;
            }
            case 'revisao':
                enviar();
                return;
            default:
                return;
        }
    }, [travado, atual, codigo, config.curriculo.obrigatorio, passo, focarPrimeiroErro, irPara, iniciar, verificar, enviar]);

    const voltar = useCallback(() => {
        if (travado || passo <= 0) return;
        // depois da verificação não se volta ao código (já confirmado): pula para os dados
        irPara(passo === INDICE_CODIGO + 1 ? INDICE_DADOS : passo - 1);
    }, [travado, passo, irPara]);

    /* ── tema (custom properties no wrapper) ─────────────────────────────────── */

    const estilo = useMemo(() => {
        const t = config.theme;
        // Destaque VISÍVEL: cor principal quase igual ao fundo (E2E 19/10, tema escuro do 1722:
        // #0d0d0f sobre #101014) sumia no número da etapa, nos chips e nas bordas. Abaixo de 3:1
        // (WCAG, elemento não textual) o destaque vira a cor do texto, e os derivados seguem ele.
        // Contraste 0 = cor que não deu para ler → mantém a principal.
        const contraste = getContrastRatio(t.primary, t.bg);
        const destaque = contraste > 0 && contraste < 3 ? t.text : t.primary;
        const vars: Record<string, string> = {
            '--tc-primary': destaque,
            '--tc-secondary': t.secondary,
            '--tc-bg': t.bg,
            '--tc-text': t.text,
            '--tc-surface': t.surface,
            '--tc-muted': t.muted,
            '--tc-on-primary': getAccessibleTextColor(destaque),
            '--tc-on-secondary': getAccessibleTextColor(t.secondary),
            '--tc-primary-suave': corComAlfa(destaque, 0.08),
            '--tc-secondary-suave': corComAlfa(t.secondary, 0.14),
            '--tc-anel': corComAlfa(destaque, 0.22),
            '--tc-anel-forte': corComAlfa(destaque, 0.45),
            // borda dos campos segue o texto do tema (o fixo escuro sumia no fundo escuro)
            '--tc-borda': corComAlfa(t.text, 0.16),
        };
        return vars as unknown as React.CSSProperties;
    }, [config.theme]);

    /* ── render ──────────────────────────────────────────────────────────────── */

    const animacao = reduzido
        ? { initial: false as const, animate: { opacity: 1 }, exit: { opacity: 1 }, transition: { duration: 0 } }
        : {
              initial: { opacity: 0, x: 24 },
              animate: { opacity: 1, x: 0 },
              exit: { opacity: 0, x: -24 },
              transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
          };

    const totalRespostas = Object.keys(estado.respostas).filter((k) => {
        const v = estado.respostas[k];
        return Array.isArray(v) ? v.length > 0 : !!String(v ?? '').trim();
    }).length;

    const rotuloBotao = enviando
        ? atual.tipo === 'revisao'
            ? 'Enviando…'
            : atual.tipo === 'dados'
              ? 'Enviando código…'
              : 'Confirmando…'
        : atual.tipo === 'dados'
          ? 'Receber código'
          : atual.tipo === 'codigo'
            ? 'Confirmar código'
            : atual.tipo === 'revisao'
              ? erroEnvio
                  ? 'Tentar de novo'
                  : 'Enviar candidatura'
              : atual.tipo === 'curriculo' && !config.curriculo.obrigatorio && !estado.curriculo
                ? 'Pular esta etapa'
                : 'Continuar';

    if (duplicado) {
        return (
            <section className={styles.wrapper} style={estilo} aria-label={config.titulo}>
                <div className={styles.card} ref={cardRef}>
                    <header className={styles.cabecalho}>
                        <h2 className={styles.titulo}>{config.titulo}</h2>
                        <p className={styles.subtitulo}>{config.subtitulo}</p>
                    </header>
                    <div className={styles.corpo} ref={corpoRef}>
                        <div className={styles.avisoInfo} role="status" style={{ marginTop: 0 }}>
                            <span className={styles.avisoIcone}>
                                <Icone nome="alerta" tamanho={18} trecho={2} />
                            </span>
                            <span>{MENSAGEM_DUPLICADO}</span>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (indisponivel !== null) {
        return (
            <section className={styles.wrapper} style={estilo} aria-label={config.titulo}>
                <div className={styles.card} ref={cardRef}>
                    <header className={styles.cabecalho}>
                        <h2 className={styles.titulo}>{config.titulo}</h2>
                        <p className={styles.subtitulo}>{config.subtitulo}</p>
                    </header>
                    <div className={styles.corpo} ref={corpoRef}>
                        <div className={styles.avisoInfo} role="status" style={{ marginTop: 0 }}>
                            <span className={styles.avisoIcone}>
                                <Icone nome="alerta" tamanho={18} trecho={2} />
                            </span>
                            <span>{indisponivel || 'As candidaturas on-line estão temporariamente indisponíveis.'}</span>
                        </div>
                        {fallbackHtml ? (
                            <div className={styles.fallback} dangerouslySetInnerHTML={{ __html: fallbackHtml }} />
                        ) : (
                            <div className={styles.fallback} />
                        )}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className={styles.wrapper} style={estilo} aria-label={config.titulo}>
            <div className={styles.card} ref={cardRef}>
                <header className={styles.cabecalho}>
                    <h2 className={styles.titulo}>{config.titulo}</h2>
                    <p className={styles.subtitulo}>{config.subtitulo}</p>
                    {!sucesso ? (
                        <div className={styles.progresso}>
                            <div
                                className={styles.progressoTrilho}
                                role="progressbar"
                                aria-valuemin={1}
                                aria-valuemax={total}
                                aria-valuenow={passo + 1}
                                aria-label={`Etapa ${passo + 1} de ${total}`}
                            >
                                <div className={styles.progressoPreenchido} style={{ width: `${((passo + 1) / total) * 100}%` }} />
                            </div>
                            <span className={styles.progressoTexto}>
                                <span>
                                    Etapa <strong>{passo + 1}</strong> de {total}
                                </span>
                                <span>{atual.titulo}</span>
                            </span>
                        </div>
                    ) : null}
                </header>

                <form
                    noValidate
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (!sucesso) continuar();
                    }}
                    onKeyDown={(e) => {
                        // Enter avança/envia (fora de textarea e botões)
                        if (e.key !== 'Enter') return;
                        const alvo = e.target as HTMLElement | null;
                        if (alvo && (alvo.tagName === 'TEXTAREA' || alvo.tagName === 'BUTTON')) return;
                        e.preventDefault();
                        if (!sucesso) continuar();
                    }}
                >
                    <div className={styles.corpo} ref={corpoRef}>
                        {!sucesso ? (
                            <>
                                <h3 className={styles.passoTitulo}>{atual.titulo}</h3>
                                <p className={styles.passoAjuda}>{atual.ajuda}</p>
                            </>
                        ) : null}

                        <AnimatePresence mode="wait" initial={false}>
                            <motion.div key={sucesso ? 'sucesso' : passo} {...animacao}>
                                {sucesso ? (
                                    <TelaSucesso nome={primeiroNome(estado.dados.nome)} mensagem={config.mensagemSucesso} reduzido={reduzido} />
                                ) : atual.tipo === 'dados' ? (
                                    <PassoDados
                                        dados={estado.dados}
                                        hp={hp}
                                        setHp={setHp}
                                        setDado={setDado}
                                        aoSair={aoSairDado}
                                        erros={erros}
                                        travado={travado}
                                        idBase={idBase}
                                        mostrarVagas={config.mostrarVagas}
                                        vagas={vagas}
                                    />
                                ) : atual.tipo === 'codigo' ? (
                                    <PassoCodigo
                                        emailMascarado={estado.emailMascarado || estado.dados.email}
                                        codigo={codigo}
                                        setCodigo={(v) => {
                                            setCodigo(v);
                                            limparErro('codigo');
                                        }}
                                        aoCompletar={(cod) => verificar(cod)}
                                        erro={erros.codigo || null}
                                        aviso={aviso}
                                        travado={travado}
                                        idBase={idBase}
                                        contador={contador}
                                        reenviando={reenviando}
                                        aoReenviar={reenviar}
                                        aoTrocarEmail={trocarEmail}
                                    />
                                ) : atual.tipo === 'curriculo' ? (
                                    <PassoCurriculo
                                        curriculo={estado.curriculo}
                                        progresso={progresso}
                                        nomeEnviando={nomeEnviando}
                                        erro={erros.curriculo || null}
                                        travado={enviando}
                                        idBase={idBase}
                                        obrigatorio={config.curriculo.obrigatorio}
                                        aoEscolher={escolherArquivo}
                                        aoTrocar={trocarArquivo}
                                    />
                                ) : atual.tipo === 'etapa' ? (
                                    <PassoEtapa
                                        etapa={atual.etapa}
                                        respostas={estado.respostas}
                                        setResposta={setResposta}
                                        aoSair={aoSairResposta}
                                        erros={erros}
                                        travado={travado}
                                        idBase={idBase}
                                    />
                                ) : (
                                    <PassoRevisao
                                        dados={estado.dados}
                                        vagas={vagas}
                                        curriculo={estado.curriculo}
                                        curriculoAtivo={config.curriculo.ativo}
                                        totalRespostas={totalRespostas}
                                        consentimento={estado.consentimento}
                                        setConsentimento={(v) => {
                                            setEstado({ consentimento: v });
                                            limparErro('consentimento');
                                        }}
                                        consentText={config.consentText}
                                        erro={erros.consentimento}
                                        travado={travado}
                                        idBase={idBase}
                                    />
                                )}
                            </motion.div>
                        </AnimatePresence>

                        <div aria-live="assertive">
                            {erroEnvio ? (
                                <div className={styles.avisoErro} role="alert">
                                    <span className={styles.avisoIcone}>
                                        <Icone nome="alerta" tamanho={18} trecho={2} />
                                    </span>
                                    <span>{erroEnvio}</span>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    {!sucesso ? (
                        <div className={styles.rodape}>
                            {passo > 0 ? (
                                <button type="button" className={`${styles.botao} ${styles.botaoSecundario}`} disabled={travado} onClick={voltar}>
                                    Voltar
                                </button>
                            ) : null}
                            <button type="submit" className={`${styles.botao} ${styles.botaoPrimario}`} disabled={travado}>
                                {enviando ? <span className={styles.spinner} aria-hidden="true" /> : null}
                                {rotuloBotao}
                            </button>
                        </div>
                    ) : null}
                </form>
            </div>
        </section>
    );
};

export default TrabalheConosco;
